import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Cable,
  Globe,
  Workflow,
  MonitorSmartphone,
  Server,
  Settings as SettingsIcon,
  Activity,
  LogOut,
  Cpu,
  KeyRound,
  Sparkles,
  Plug,
  Waypoints,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useHealthSummary } from "../../lib/useHealthSummary";
import { TID } from "../../lib/testIds";
import { cn } from "../../lib/utils";

const NAV = [
  { to: "/", key: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/connections", key: "connections", label: "Conexiones", icon: Cable },
  { to: "/web-services", key: "web-services", label: "Servicios web", icon: Globe },
  { to: "/sessions", key: "sessions", label: "Sesiones", icon: KeyRound },
  { to: "/ai-models", key: "ai-models", label: "Modelos IA", icon: Sparkles },
  { to: "/providers", key: "providers", label: "Proveedores IA", icon: Plug },
  { to: "/n8n", key: "n8n", label: "n8n", icon: Workflow },
  { to: "/browser", key: "browser", label: "Navegador / Playwright", icon: MonitorSmartphone },
  { to: "/krai-api", key: "krai-api", label: "KRAI API", icon: Server },
  { to: "/gateway", key: "gateway", label: "KRAI API Gateway", icon: Waypoints },
  { to: "/engines", key: "engines", label: "Motores IA", icon: Cpu },
  { to: "/settings", key: "settings", label: "Configuración", icon: SettingsIcon },
  { to: "/admin", key: "admin", label: "Administración", icon: Activity },
];

export default function Sidebar({ mobileOpen = false, onCloseMobile }) {
  const { logout } = useAuth();
  const location = useLocation();
  // Backend-wide health signal for the sidebar dot next to "Administración".
  // Severity ladder: ok (none) · warning (amber, >=80% RAM or 1 stuck or 2+
  // recent errors) · critical (red pulsing, >=90% RAM or 3+ stuck or 5+ errors).
  const health = useHealthSummary();

  // Close drawer whenever the user picks a link (mobile only).
  const closeOnNav = () => {
    if (typeof onCloseMobile === "function") onCloseMobile();
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          onClick={onCloseMobile}
          className="md:hidden fixed inset-0 z-40 bg-black/60"
          aria-hidden="true"
        />
      )}

      <aside
        data-testid={TID.layout.sidebar}
        className={cn(
          "w-[260px] shrink-0 border-r border-zinc-800 bg-zinc-950 flex flex-col",
          // Mobile: fixed drawer that slides in from the left
          "fixed inset-y-0 left-0 z-50 h-screen transform transition-transform duration-200",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          // Desktop: sticky column, always visible
          "md:sticky md:top-0 md:translate-x-0 md:z-auto"
        )}
      >
        <div className="px-5 py-5 border-b border-zinc-800 flex items-center gap-2.5 justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-8 w-8 rounded-md bg-white text-black grid place-items-center shrink-0">
              <Cpu className="h-4 w-4" />
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-sm font-semibold tracking-tight truncate">KRAI CONNECT</div>
              <div className="text-[11px] text-zinc-500 font-mono">v0.1.0 · privado</div>
            </div>
          </div>
          <button
            onClick={onCloseMobile}
            className="md:hidden p-1 -mr-1 text-zinc-400 hover:text-white"
            aria-label="Cerrar menú"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 px-3 mb-2">
            Panel
          </div>
          <ul className="space-y-0.5">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.key}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={closeOnNav}
                    data-testid={TID.layout.navLink(item.key)}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150",
                        isActive
                          ? "bg-zinc-900 text-white border border-zinc-800"
                          : "text-zinc-400 hover:text-white hover:bg-zinc-900/60 border border-transparent"
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    <span className="flex-1">{item.label}</span>
                    {item.key === "admin" && health.severity !== "ok" && (
                      <span
                        data-testid="sidebar-admin-alert"
                        title={health.reasons.join(" · ")}
                        className={cn(
                          "inline-block h-2 w-2 rounded-full",
                          health.severity === "critical"
                            ? "bg-red-500 animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.9)]"
                            : "bg-amber-400"
                        )}
                      />
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <div className="mt-6">
            <NavLink
              to="/system"
              onClick={closeOnNav}
              data-testid={TID.layout.statusBadge}
              className={({ isActive }) =>
                cn(
                  "flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm border transition-colors duration-150",
                  isActive
                    ? "bg-zinc-900 text-white border-zinc-800"
                    : "border-zinc-800/60 text-zinc-400 hover:text-white hover:bg-zinc-900/60"
                )
              }
            >
              <span className="inline-flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Hosting y mantenimiento
              </span>
              <span className="krai-dot krai-dot-live bg-emerald-500" />
            </NavLink>
          </div>
        </nav>

        <div className="border-t border-zinc-800 p-3">
          <button
            data-testid={TID.layout.logout}
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors duration-150 border border-transparent hover:border-zinc-800"
          >
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </button>
          <div className="mt-2 px-3 text-[10px] font-mono text-zinc-600 truncate">
            {location.pathname}
          </div>
        </div>
      </aside>
    </>
  );
}
